import { useEffect, useRef, useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { mutationErrorMessage, normalizeApiError } from '@/features/common/apiError';
import { useCan } from '@/features/permissions/useCan';

import {
  useAttachSubProjectMutation,
  useCreateReportProjectMutation,
  useGetReportProjectsQuery,
  useGetSubProjectResourcesQuery,
} from './reportsApi';
import { REPORTS_PERMISSIONS } from './types';

const CREATE_SENTINEL = '__create__';
const ATTACH_422_MESSAGE = "This report project can't be attached here.";
const CREATE_THEN_ATTACH_FAILED_MESSAGE =
  'Report project created, but attaching failed — try attaching it again.';

/**
 * "Add sub-project" dialog (design §6.2) — one entry point, two modes (G1
 * progressive disclosure): a Select over the attachable report-projects (by
 * `projectId` minus attached) with "Create a new one…" revealing the Name
 * field. When nothing is attachable and create is denied, the dialog offers
 * only Cancel (no dead primary).
 */
export function AddSubProjectDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
}) {
  const toast = useToast();
  const resourcesQuery = useGetSubProjectResourcesQuery(projectId);
  const projectsQuery = useGetReportProjectsQuery(projectId);
  const canCreate = useCan(
    REPORTS_PERMISSIONS.projectCreate.action,
    REPORTS_PERMISSIONS.projectCreate.resource,
  );

  const [createReportProject, createState] = useCreateReportProjectMutation();
  const [attach, attachState] = useAttachSubProjectMutation();

  const [picked, setPicked] = useState<string | null>(null);
  // E1 two-phase honesty: a project created here whose ATTACH failed. Retrying
  // attaches it again — it must never silently re-create a second project.
  const [pendingCreatedId, setPendingCreatedId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>();
  const [selectError, setSelectError] = useState<string | undefined>();
  const [inlineAlert, setInlineAlert] = useState<string | undefined>();
  const nameRef = useRef<HTMLInputElement>(null);

  const attachedRefIds = new Set((resourcesQuery.data ?? []).map((r) => r.refId));
  const attachable = (projectsQuery.data ?? []).filter((p) => !attachedRefIds.has(p.id));
  const hasAttachable = attachable.length > 0;
  const createOptionVisible = canCreate.allowed === true;

  // Effective selection: honour the user's pick when still valid, otherwise the
  // first attachable project (wireframe default), otherwise create mode.
  const pickedValid =
    picked === CREATE_SENTINEL || (picked !== null && attachable.some((p) => p.id === picked));
  const selectedId = pickedValid ? picked : (attachable[0]?.id ?? null);
  const isCreateMode =
    selectedId === CREATE_SENTINEL || (selectedId === null && createOptionVisible);
  const nothingPossible = !hasAttachable && !createOptionVisible;

  useEffect(() => {
    if (open) {
      setPicked(null);
      setPendingCreatedId(null);
      setName('');
      setNameError(undefined);
      setSelectError(undefined);
      setInlineAlert(undefined);
    }
  }, [open]);

  const isBusy = createState.isLoading || attachState.isLoading;

  async function attachRefId(refId: string): Promise<boolean> {
    try {
      await attach({ projectId, refId }).unwrap();
      toast.success('Sub-project added.');
      onOpenChange(false);
      return true;
    } catch (error) {
      if (normalizeApiError(error).status === 422) {
        // A foreign/invalid refId — inline next to the Select (D5).
        setSelectError(ATTACH_422_MESSAGE);
      } else {
        toast.error(mutationErrorMessage(error, "Couldn't add sub-project."));
      }
      return false;
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (isBusy || nothingPossible) {
      return;
    }
    setSelectError(undefined);
    setInlineAlert(undefined);

    if (isCreateMode) {
      // A previous create succeeded but its attach failed → retry the ATTACH.
      if (pendingCreatedId) {
        const retried = await attachRefId(pendingCreatedId);
        if (retried) {
          setPendingCreatedId(null);
          setInlineAlert(undefined);
        }
        return;
      }

      if (!name.trim()) {
        setNameError('Enter a name.');
        nameRef.current?.focus();
        return;
      }
      setNameError(undefined);

      let createdId: string | undefined;
      try {
        const created = await createReportProject({ name: name.trim(), projectId }).unwrap();
        createdId = created?.id;
      } catch (error) {
        toast.error(mutationErrorMessage(error, "Couldn't create report project."));
        return;
      }

      if (!createdId) {
        toast.error("Couldn't create report project.");
        return;
      }

      const attached = await attachRefId(createdId);
      if (!attached) {
        // E1: keep the dialog open with the honest two-phase message. The
        // created project is remembered so a retry attaches it, never re-creates.
        setPendingCreatedId(createdId);
        setInlineAlert(CREATE_THEN_ATTACH_FAILED_MESSAGE);
        setName('');
      }
      return;
    }

    if (selectedId) {
      await attachRefId(selectedId);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !isBusy && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add sub-project</DialogTitle>
          <DialogDescription>
            Attach a report project to collect issue reports for {projectName}.
          </DialogDescription>
        </DialogHeader>

        {inlineAlert ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            {inlineAlert}
          </div>
        ) : null}

        {nothingPossible ? null : (
          <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
            {hasAttachable ? (
              <div className="space-y-2">
                <Label htmlFor="sub-project-select">Report project</Label>
                <Select value={selectedId ?? undefined} onValueChange={setPicked} disabled={isBusy}>
                  <SelectTrigger
                    id="sub-project-select"
                    aria-invalid={Boolean(selectError)}
                    aria-describedby={selectError ? 'sub-project-select-error' : undefined}
                  >
                    <SelectValue placeholder="Select a report project" />
                  </SelectTrigger>
                  <SelectContent>
                    {createOptionVisible ? (
                      <SelectItem value={CREATE_SENTINEL}>Create a new one…</SelectItem>
                    ) : null}
                    {attachable.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectError ? (
                  <p
                    id="sub-project-select-error"
                    role="alert"
                    className="text-xs text-destructive"
                  >
                    {selectError}
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                A new report project will be created and attached.
              </p>
            )}

            {isCreateMode ? (
              <div className="space-y-2">
                <Label htmlFor="sub-project-name">Name</Label>
                <Input
                  id="sub-project-name"
                  ref={nameRef}
                  placeholder="e.g. Website contact form"
                  maxLength={200}
                  value={name}
                  aria-invalid={Boolean(nameError)}
                  aria-describedby={nameError ? 'sub-project-name-error' : 'sub-project-name-help'}
                  onChange={(event) => setName(event.target.value)}
                />
                {nameError ? (
                  <p id="sub-project-name-error" role="alert" className="text-xs text-destructive">
                    {nameError}
                  </p>
                ) : (
                  <p id="sub-project-name-help" className="text-sm text-muted-foreground">
                    Shown in the sub-projects list.
                  </p>
                )}
              </div>
            ) : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isBusy}>
                {isBusy ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Adding…
                  </>
                ) : (
                  'Add sub-project'
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {nothingPossible ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
